export type FieldBadgeConfiguration = 'anchor' | 'report';

export const FIELD_BADGE_SIZE = 20;

/**
 * Breathing room the badge keeps from the innermost decoration of a canvas element. 3px is the
 * smallest value that puts the square badge corner inside an 8px `rounded-lg` curve:
 * sqrt(5^2 + 5^2) = 7.07 < 8.
 */
export const FIELD_BADGE_GAP = 3;

/** Table cells have square corners and much less room, so the badge hugs their borders closer. */
export const FIELD_BADGE_CELL_GAP = 1;

export interface FieldBadgeInset {
  top: number;
  right: number;
}

export function fieldBadgeCornerStyle(inset?: FieldBadgeInset): Record<string, string> {
  return {
    top: `${FIELD_BADGE_GAP + (inset?.top ?? 0)}px`,
    right: `${FIELD_BADGE_GAP + (inset?.right ?? 0)}px`,
  };
}

export interface FieldBadgeOffset {
  top: number;
  left: number;
}

export function computeFieldBadgeOffset(
  target: HTMLElement | null,
  root: HTMLElement | null
): FieldBadgeOffset | null {
  if (!target || !root) return null;
  if (target.offsetWidth <= 0) return null;

  let top = 0;
  let left = 0;
  let node: HTMLElement | null = target;

  while (node && node !== root) {
    top += node.offsetTop;
    left += node.offsetLeft;
    node = node.offsetParent as HTMLElement | null;
  }

  if (node !== root) return null;

  // `clientTop` / `clientLeft` / `clientWidth` move the anchor from the target's border box to its
  // padding box, so the badge never sits on the cell's own border. Reading them from the DOM covers
  // every `borderPattern` and page-break-chunk edge case without re-deriving the border widths.
  return {
    top: top + target.clientTop,
    left: left + target.clientLeft + target.clientWidth - FIELD_BADGE_SIZE,
  };
}
